import { createFileRoute, Link } from '@tanstack/react-router'
import { Map, MapPin, Globe } from 'lucide-react'
import type { ReactNode } from 'react'
import { navLabel } from '@region13/ui'
import { Card } from '../components/ui/Card'
import { FIELDS_NAV, useNavTitle } from '../lib/nav'

// The landing page for fields.ayso13.org. '/' used to BE the map editor (served
// by the worker), which left every other page in this shell — venues, layouts,
// subfield reconcile — unreachable for a signed-in field manager: the worker
// answered '/' before the SPA could, and nothing linked onward. This is the fork
// in the road instead; the editor now lives at '/map'.
export const Route = createFileRoute('/')({ component: FieldManagementHome })

function FieldManagementHome() {
  const title = useNavTitle()
  return (
    <div className="flex flex-col gap-4">
      <div>
        <h1 className="text-lg font-extrabold text-ink">{title}</h1>
        <p className="text-sm text-ink-soft">What would you like to do?</p>
      </div>

      {/* The map editor is a full-page vanilla-JS app served by the worker, not a
          route in this router, so it needs a plain anchor rather than a <Link>. */}
      <Choice
        as="a"
        href="/map"
        icon={<Map size={20} />}
        title={navLabel(FIELDS_NAV, '/map')}
        body="Draw and save field layouts, export PDFs, and publish maps to the website."
      />

      <Choice
        as="link"
        to="/venues"
        icon={<MapPin size={20} />}
        title={navLabel(FIELDS_NAV, '/venues')}
        body="Venue names, addresses, and locations, plus layouts and InLeague field matching."
      />

      <Card className="border-brand-gold/40">
        <div className="flex items-start gap-3">
          <span className="mt-0.5 shrink-0 text-brand-maroon"><Globe size={20} /></span>
          <div className="min-w-0">
            <h2 className="text-sm font-semibold text-ink">Publishing to ayso13.org</h2>
            <p className="text-sm text-ink-soft">
              Saved maps are live here straight away, but the public website is a static build.
              Use <span className="font-semibold">Publish Site</span> in the map editor to push
              them live.
            </p>
          </div>
        </div>
      </Card>
    </div>
  )
}

/** One option on the landing page. Two flavours because the map editor is a
 *  worker-served page (plain anchor) while everything else is a router route. */
function Choice(
  props: { icon: ReactNode; title: string; body: string } & (
    | { as: 'a'; href: string }
    | { as: 'link'; to: string }
  ),
) {
  const inner = (
    <Card className="hover:border-sidebar">
      <div className="flex items-start gap-3">
        <span className="mt-0.5 shrink-0 text-brand-maroon">{props.icon}</span>
        <div className="min-w-0">
          <h2 className="font-semibold text-ink">{props.title}</h2>
          <p className="text-sm text-ink-soft">{props.body}</p>
        </div>
      </div>
    </Card>
  )
  return props.as === 'a' ? (
    <a href={props.href} className="block">{inner}</a>
  ) : (
    <Link to={props.to} className="block">{inner}</Link>
  )
}
